import { Popover, PopoverContent, PopoverTrigger } from './Popover';
import { cn } from '@ais-ui/utils';
import React, { useEffect, useState } from 'react';
import { Paintbrush } from 'lucide-react';
import { Button } from './Button';
import { Input } from './Input';
import { baseColours } from '@ais-ui/basecolours';
import { toHexColor } from './toHexColor';

function ColorPicker({
  background,
  setBackground,
  className,
}: {
  background: string;
  setBackground: (background: string) => void;
  className?: string;
}) {
  const solids = [
    '#003b5c',
    '#0057e5',
    '#41b6e6',
    '#3ea908',
    '#f0cc2e',
    '#ec894d',
    '#e50000',
    '#e641b6',
  ];

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant={'outline'}
          className={cn(
            'w-[220px] justify-start text-left font-normal',
            !background && 'text-muted-foreground',
            className,
          )}
        >
          <div className="w-full flex items-center justify-center gap-2">
            {background ? (
              <div
                className="h-6 w-6 rounded transition-all"
                style={{ background }}
              ></div>
            ) : (
              <Paintbrush className="h-6 w-6" />
            )}
          </div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64">
        <div className="mt-0 flex flex-wrap gap-1">
          {solids.map((s) => (
            <div
              key={s}
              style={{ background: s }}
              className="rounded-md h-6 w-6 cursor-pointer active:scale-105"
              onClick={() => setBackground(s)}
            />
          ))}
        </div>
        <Input
          id="custom"
          value={background}
          className="col-span-2 h-8 mt-4"
          onChange={(e) => setBackground(e.currentTarget.value)}
          onBlur={(e) => {
            const hex = toHexColor(e.currentTarget.value);
            if (hex !== background) setBackground(hex);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}

const GradientButton = ({
  background,
  children,
}: {
  background: string;
  children: React.ReactNode;
}) => {
  return (
    <div
      className="p-0.5 rounded-md relative !bg-cover !bg-center transition-all"
      style={{ background }}
    >
      <div className="bg-popover/80 rounded-md p-1 text-xs text-center">
        {children}
      </div>
    </div>
  );
};

function LineColorPicker({
  background,
  setBackground,
  className,
}: {
  background: string;
  setBackground: (background: string) => void;
  className?: string;
}) {
  const initialSolids = [
    '#003b5c',
    '#0057e5',
    '#41b6e6',
    '#3ea908',
    '#f0cc2e',
    '#ec894d',
    '#e50000',
    '#e641b6',
  ];

  const [solids, setSolids] = useState(initialSolids);

  useEffect(() => {
    const isHexColor = /^#([0-9A-F]{3}){1,2}$/i.test(background);

    if (isHexColor && !initialSolids.includes(background)) {
      if (!solids.includes(background)) {
        setSolids([background, ...solids]);
      }
    } else {
      setSolids(initialSolids);
    }
  }, [background]);

  return (
    <div>
      <Input
        id="custom"
        value={background}
        className="h-8 mt-4"
        onChange={(e) => setBackground(e.currentTarget.value)}
        onBlur={(e) => {
          const hex = toHexColor(e.currentTarget.value);
          if (hex !== background) setBackground(hex);
        }}
      />
      <div className="flex flex-wrap gap-4 p-4">
        {solids.map((color) => (
          <div
            key={color}
            className={`w-6 h-6 rounded-full cursor-pointer transition-all relative ${
              background === color
                ? 'ring-2 ring-primary selected'
                : 'hover:scale-105 hover:shadow-md'
            }`}
            style={{ backgroundColor: color }}
            onClick={() => setBackground(color)}
          >
            {background === color && (
              <span className="checkmark absolute inset-0 flex items-center justify-center text-white">
                &#10003;
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function LineColorPickerWithNames({
  background,
  setBackground,
  className,
}: {
  background: string;
  setBackground: (background: string) => void;
  className?: string;
}) {
  const colorNames = Object.keys(baseColours);

  useEffect(() => {
    if (!colorNames.includes(background)) {
      setBackground('blue');
    }
  }, [background, colorNames, setBackground]);

  return (
    <div className={cn('flex flex-wrap gap-4 p-4', className)}>
      {colorNames.map((color) => (
        <div
          id={color}
          key={color}
          className={`w-6 h-6 rounded-full cursor-pointer transition-all relative ${
            background === color
              ? 'ring-2 ring-primary selected'
              : 'hover:scale-105 hover:shadow-md'
          }`}
          style={{
            backgroundColor: baseColours[color as keyof typeof baseColours],
          }}
          onClick={() => setBackground(color)}
        >
          {background === color && (
            <span className="checkmark absolute inset-0 flex items-center justify-center text-white">
              &#10003;
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

export { ColorPicker, LineColorPicker, LineColorPickerWithNames };
